import { useMutation, useQueryClient } from 'react-query'

import { attachmentRepository } from '@webapp/core/data-access/repositories/AttachmentRepository'
import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type CreditCardItemFormSchema } from '@webapp/modules/accounts-payable/components/CreditCardItemForm'
import { mapUpdateCreditCardItemFormSchemaToPayload } from '@webapp/modules/accounts-payable/data-access/mappers/mapCreditCardItemFormSchemaToPayload'
import { creditCardItemRepository } from '@webapp/modules/accounts-payable/data-access/repositories/CreditCardItemRepository'
import { CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import {
  CREDIT_CARD_ITEM_LIST_BASE_KEY,
  CREDIT_CARD_ITEM_UNMATCHED_BASE_KEY,
} from '@webapp/modules/accounts-payable/query-configs/creditCardItemQueries'
import { RECONCILE_SESSION_MATCHES_BASE_KEY } from '@webapp/modules/accounts-payable/query-configs/reconcileSessionQueries'

const readFileContent = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

// Attachment uploads happen inside the command flow — new UUIDs are appended
// to the item's existing packing_slips (BHV-018).
const uploadAttachments = async (files: File[]): Promise<string[]> => {
  const uploads = await Promise.all(
    files.map(async (file) =>
      attachmentRepository.upload({
        name: file.name,
        type: file.type,
        file: await readFileContent(file),
      })
    )
  )

  return uploads.map((attachment) => attachment.uuid)
}

interface UpdateCreditCardItemVariables {
  item: CreditCardItem
  mode: 'edit' | 'view'
  schema: CreditCardItemFormSchema
}

export const useMutateUpdateCreditCardItem = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    CreditCardItem,
    RepoError,
    UpdateCreditCardItemVariables
  >({
    mutationFn: async ({ item, mode, schema }) => {
      const uploadedUuids =
        mode === 'edit' ? await uploadAttachments(schema.attachments) : []

      const updated = await creditCardItemRepository.update(
        item.id,
        mapUpdateCreditCardItemFormSchemaToPayload({
          schema,
          itemId: item.id,
          mode,
          packingSlipUuids: [
            ...item.packingSlips.map((slip) => slip.uuid),
            ...uploadedUuids,
          ],
        })
      )

      return new CreditCardItem(updated)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CREDIT_CARD_ITEM_LIST_BASE_KEY,
      })
      queryClient.invalidateQueries({
        queryKey: CREDIT_CARD_ITEM_UNMATCHED_BASE_KEY,
      })
      // Matched-item edits must refresh the session's matched rows (2.3).
      queryClient.invalidateQueries({
        queryKey: RECONCILE_SESSION_MATCHES_BASE_KEY,
      })
    },
  })

  return {
    updateCreditCardItem: mutation.mutateAsync,
    isUpdating: mutation.isLoading,
    error: mutation.error,
  }
}
